import { useState } from "react";
import dayjs from "dayjs";
import styles from "../mybooking.module.css";
import { Calendar, MapPin, XCircle, ChevronDown } from "react-feather";
import ActionMenu from "./ActionMenu";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";

const STAGES = [
    "Booked",
    "Assigned",
    "Picked up",
    "In warehouse",
    "Shipped",
    "At port GH",
    "Received GH",
    "Out for delivery",
    "Delivered",
];

// Booking status -> stepper index. Status 9 (Received At Port) was added
// later, so it sits between "Shipped" (4) and "Received GH" (5).
// Status 8 (Partially Delivered) isn't a step of its own on this stepper —
// it sits between "Out for delivery" (6) and "Delivered" (7). Everything
// up to and including "Out for delivery" is done, "Delivered" is shown as
// in-progress rather than complete.
const STEPPER_INDEX = { 0: 0, 1: 1, 2: 2, 3: 3, 4: 4, 9: 5, 5: 6, 6: 7, 7: 8, 8: 7 };
const getStepperIndex = (status) => STEPPER_INDEX[status] ?? status;
const isPartial = (status) => status === 8;

const StatusStepper = ({ status }) => {
    const current = getStepperIndex(parseInt(status));
    const partial = isPartial(parseInt(status));

    return (
        <div className={styles.Stepper}>
            {STAGES.map((label, index) => {
                const done = index < current || (partial && index === current);
                const isCurrent = partial ? index === current + 1 : index === current;
                return (
                    <div className={styles.StepperItem} key={label}>
                        <span className={`${styles.StepperLine} ${done || isCurrent ? styles.Done : ""}`} />
                        <span
                            className={`${styles.StepperDot} ${done ? styles.Done : ""} ${isCurrent ? styles.Current : ""} ${partial && isCurrent ? styles.Partial : ""}`}
                        />
                        <span className={`${styles.StepperLabel} ${isCurrent ? styles.Current : ""}`}>
                            {partial && isCurrent ? "Partially delivered" : label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

const BookingBlock = ({
    item,
    setViewModalOpen,
    setViewId,
    setViewStr,
    setSendInvoiceModalOpen,
    setInvoiceMail,
    setInvoiceMailId,
    printBooking,
    setCancelModalOpen,
    setCancelId,
}) => {
    const [open, setOpen] = useState(false);

    const status = parseInt(item?.status);
    const isCancelled = status === -1;
    const isDelivered = status === 7;
    const partial = isPartial(status);
    const due = parseFloat(item?.due_balance);

    const stepperIndex = getStepperIndex(status);
    const progressPct = isCancelled ? 0 : Math.min(100, Math.round(((stepperIndex + (partial ? 0.5 : 1)) / STAGES.length) * 100));

    const statusLabel = isCancelled ? "Cancelled" : partial ? "Partially delivered" : STAGES[stepperIndex] || "—";

    const isAir = item?.freight_type === 'Air Freight';

    return (
        <div className={styles.Row}>
            <div className={styles.RowMain} onClick={() => setOpen((v) => !v)}>
                {/* Desktop columns */}
                <div className={styles.RowId}>
                    {item?.booking_id}
                    <div className={styles.RowIdBadges}>
                        {isCancelled ? (
                            <span className={styles.Cancelled}>Cancelled</span>
                        ) : parseInt(item?.payment_status) === 1 ? (
                            <span className={styles.Paid}>Paid</span>
                        ) : (
                            <span className={styles.Unpaid}>Unpaid</span>
                        )}
                        {isDelivered && <span className={`${styles.Stamp} ${styles.Delivered}`}>Delivered</span>}
                        {partial && <span className={styles.Pending}>Partial</span>}
                        <span className={`${styles.FreightBadge} ${isAir ? styles.FreightAir : styles.FreightSea}`}>
                            {isAir ? <FlightTakeoffIcon /> : <DirectionsBoatIcon />}
                            {isAir ? 'Air' : 'Sea'}
                        </span>
                    </div>
                </div>

                <div className={styles.RowStatus}>
                    <div className={styles.RowStatusTrack}>
                        <div
                            className={`${styles.RowStatusFill} ${isCancelled ? styles.Cancelled : ""} ${partial ? styles.Partial : ""}`}
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                    <span className={styles.RowStatusLabel}>{statusLabel}</span>
                </div>

                <div className={styles.RowDate}>{dayjs(item?.created_at).format("DD/MM/YYYY")}</div>
                <div className={styles.RowTotal}>£{parseFloat(item?.total).toFixed(2)}</div>
                <div className={`${styles.RowDue} ${due > 0 ? styles.HasDue : ""}`}>£{due.toFixed(2)}</div>

                {/* Mobile stacked summary */}
                <div className={styles.RowMainMobileTop}>
                    <span className={styles.RowId} style={{ display: "block" }}>
                        {item?.booking_id}
                    </span>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span className={`${styles.FreightBadge} ${isAir ? styles.FreightAir : styles.FreightSea}`}>
                            {isAir ? <FlightTakeoffIcon /> : <DirectionsBoatIcon />}
                            {isAir ? 'Air' : 'Sea'}
                        </span>
                        {isCancelled ? (
                            <span className={styles.Cancelled}>Cancelled</span>
                        ) : parseInt(item?.payment_status) === 1 ? (
                            <span className={styles.Paid}>Paid</span>
                        ) : (
                            <span className={styles.Unpaid}>Unpaid</span>
                        )}
                    </div>
                </div>
                <div className={styles.RowMainMobileBottom}>
                    <span>{dayjs(item?.created_at).format("DD/MM/YYYY")} · {statusLabel}</span>
                    <span>£{parseFloat(item?.total).toFixed(2)}</span>
                </div>

                <ChevronDown className={`${styles.RowChevron} ${open ? styles.Open : ""}`} size={18} />
            </div>

            {open && (
                <div className={styles.RowDetail}>
                    <div className={styles.TicketDivider} />

                    {isCancelled ? (
                        <div className={styles.CancelledBanner}>
                            <XCircle size={16} /> This booking was cancelled
                        </div>
                    ) : (
                        <StatusStepper status={status} />
                    )}

                    <div className={styles.DetailGrid}>
                        <div className={styles.DetailCol}>
                            <p className={styles.DetailTitle}>Order summary</p>
                            <p className={styles.DetailLine}>
                                <span>Order date</span>
                                <span className={styles.DetailLineValue}>{dayjs(item?.created_at).format("DD/MM/YYYY")}</span>
                            </p>
                            <p className={styles.DetailLine}>
                                <span>Total amount</span>
                                <span className={styles.DetailLineValue}>£{parseFloat(item?.total).toFixed(2)}</span>
                            </p>
                            <p className={styles.DetailLine}>
                                <span>Paid amount</span>
                                <span className={styles.DetailLineValue}>£{parseFloat(item?.paid_amount).toFixed(2)}</span>
                            </p>
                            <p className={styles.DetailLine}>
                                <span>Due balance</span>
                                <span className={styles.DetailLineValue}>£{due.toFixed(2)}</span>
                            </p>
                        </div>

                        <div className={styles.DetailCol}>
                            <p className={styles.DetailTitle}>Pickup details</p>
                            <p className={styles.DetailAddr}>
                                <Calendar /> {dayjs(item?.created_at).format("DD/MM/YYYY LT")}
                            </p>
                            <p className={styles.DetailAddr}>
                                <MapPin /> {item?.pickup_details?.address}
                            </p>
                        </div>
                    </div>

                    <div className={styles.DetailActions} onClick={(e) => e.stopPropagation()}>
                        <ActionMenu
                            item={item}
                            setViewModalOpen={setViewModalOpen}
                            setViewId={setViewId}
                            setViewStr={setViewStr}
                            setSendInvoiceModalOpen={setSendInvoiceModalOpen}
                            setInvoiceMailId={setInvoiceMailId}
                            setInvoiceMail={setInvoiceMail}
                            printBooking={printBooking}
                            setCancelId={setCancelId}
                            setCancelModalOpen={setCancelModalOpen}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingBlock;